import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { supabase } from '../../lib/supabaseClient';
import * as api from '../../api';
import Spinner from '../Atoms/Spinner';
import Section from '../Atoms/Section';
import { generateFD100KVDataURL } from '../Utils/generateFD100KV';

// Animations
const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const pulse = keyframes`
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
`;

const shimmer = keyframes`
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
`;

const checkmark = keyframes`
    0% {
        stroke-dashoffset: 100;
    }
    100% {
        stroke-dashoffset: 0;
    }
`;

// Styled Components
const Container = styled.div`
    max-width: 700px;
    margin: 0 auto;
    padding: 40px 20px;
    animation: ${fadeIn} 0.6s ease-out;
`;

const LogoHeader = styled.div`
    text-align: center;
    margin-bottom: 40px;

    img {
        max-width: 180px;
        margin-bottom: 20px;
    }
`;

const Card = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
`;

const CardHeader = styled.div`
    background: linear-gradient(135deg, #0a0a15 0%, #1a1a2e 100%);
    color: white;
    padding: 35px 30px;
    text-align: center;

    h1 {
        font-size: 26px;
        font-weight: 700;
        margin: 0 0 10px 0;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        letter-spacing: 0.5px;
                color: rgba(255, 255, 255, 0.95);

    }

    .subtitle {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.95);
        font-weight: 500;
    }
`;

const CardBody = styled.div`
    padding: 40px;
`;

const PartenaireInfo = styled.div`
    text-align: center;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #eee;

    .name {
        font-size: 28px;
        font-weight: 700;
        color: #1a1a2e;
        margin-bottom: 8px;
    }

    .badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: linear-gradient(135deg, #3498db, #2980b9);
        color: white;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
    }
`;

const ExistingLogo = styled.div`
    text-align: center;
    margin-bottom: 24px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 12px;
    border: 2px solid #e9ecef;

    .label {
        font-size: 14px;
        color: #666;
        margin-bottom: 12px;
        font-weight: 500;
    }

    img {
        max-width: 200px;
        max-height: 120px;
        object-fit: contain;
        border-radius: 8px;
    }
`;

const Instructions = styled.div`
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 32px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #1a1a2e;
        margin: 0 0 16px 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    ul {
        margin: 0;
        padding-left: 20px;
        color: #555;
        line-height: 1.8;
    }

    li {
        margin-bottom: 8px;
    }
`;

const DropZone = styled.div`
    border: 3px dashed ${props => props.isDragActive ? '#28a745' : props.hasFile ? '#28a745' : '#dee2e6'};
    border-radius: 16px;
    padding: 48px 32px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: ${props => props.isDragActive ? 'rgba(40, 167, 69, 0.05)' : props.hasFile ? 'rgba(40, 167, 69, 0.02)' : '#fafafa'};
    position: relative;
    overflow: hidden;

    &:hover {
        border-color: #28a745;
        background: rgba(40, 167, 69, 0.03);
        transform: translateY(-2px);
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(90deg, transparent, rgba(40, 167, 69, 0.1), transparent);
        background-size: 200% 100%;
        animation: ${props => props.isDragActive ? shimmer : 'none'} 1.5s infinite;
    }

    .icon {
        font-size: 56px;
        margin-bottom: 16px;
        display: block;
    }

    .main-text {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
    }

    .sub-text {
        font-size: 14px;
        color: #666;
    }

    input {
        display: none;
    }
`;

const PreviewContainer = styled.div`
    margin-top: 24px;
    text-align: center;
    animation: ${fadeIn} 0.4s ease-out;

    .preview-label {
        font-size: 14px;
        font-weight: 600;
        color: #28a745;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .preview-image {
        max-width: 280px;
        max-height: 180px;
        object-fit: contain;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        border: 2px solid #28a745;
    }

    .file-name {
        margin-top: 12px;
        font-size: 13px;
        color: #666;
        background: #f8f9fa;
        padding: 8px 16px;
        border-radius: 20px;
        display: inline-block;
    }

    .change-button {
        display: block;
        margin: 16px auto 0;
        padding: 8px 20px;
        background: transparent;
        border: 1px solid #dee2e6;
        border-radius: 20px;
        font-size: 13px;
        color: #666;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            background: #f8f9fa;
            border-color: #adb5bd;
        }
    }
`;

const SubmitButton = styled.button`
    width: 100%;
    padding: 18px 32px;
    background: ${props => props.disabled ? '#adb5bd' : 'linear-gradient(135deg, #28a745, #20c997)'};
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 700;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    margin-top: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    &:not(:disabled):hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(40, 167, 69, 0.35);
    }

    &:not(:disabled):active {
        transform: translateY(-1px);
    }
`;

const SuccessContainer = styled.div`
    text-align: center;
    padding: 60px 40px;
    animation: ${fadeIn} 0.6s ease-out;

    .success-icon {
        width: 100px;
        height: 100px;
        margin: 0 auto 24px;
        background: linear-gradient(135deg, #28a745, #20c997);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: ${pulse} 0.6s ease-out;

        svg {
            width: 50px;
            height: 50px;

            path {
                stroke: white;
                stroke-width: 3;
                stroke-linecap: round;
                stroke-linejoin: round;
                fill: none;
                stroke-dasharray: 100;
                animation: ${checkmark} 0.6s ease-out 0.3s forwards;
                stroke-dashoffset: 100;
            }
        }
    }

    h2 {
        font-size: 28px;
        font-weight: 700;
        color: #1a1a2e;
        margin-bottom: 12px;
    }

    .message {
        font-size: 16px;
        color: #666;
        line-height: 1.6;
        margin-bottom: 32px;
    }

    .logo-preview {
        max-width: 200px;
        max-height: 120px;
        object-fit: contain;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        margin-bottom: 32px;
    }

    .next-steps {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-radius: 12px;
        padding: 24px;
        text-align: left;

        h3 {
            font-size: 16px;
            font-weight: 600;
            color: #1a1a2e;
            margin: 0 0 12px 0;
        }

        p {
            margin: 0;
            color: #555;
            line-height: 1.6;
            font-size: 14px;
        }
    }
`;

const ErrorContainer = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #dc3545;

    .icon {
        font-size: 64px;
        margin-bottom: 16px;
    }

    .title {
        font-size: 24px;
        font-weight: bold;
        margin-bottom: 8px;
    }

    .message {
        font-size: 16px;
        color: #666;
    }
`;

const Footer = styled.div`
    text-align: center;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #eee;
    color: #888;
    font-size: 13px;
    line-height: 1.6;

    a {
        color: #3498db;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const KVPreviewSection = styled.div`
    margin-top: 32px;
    padding: 24px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 12px;
    border: 2px solid #28a745;

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: #1a1a2e;
        margin: 0 0 8px 0;
        text-align: center;
    }

    .kv-subtitle {
        font-size: 14px;
        color: #666;
        text-align: center;
        margin-bottom: 20px;
    }
`;

const KVGrid = styled.div`
    display: flex;
    gap: 20px;
    justify-content: center;
    flex-wrap: wrap;
`;

const KVCard = styled.div`
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    max-width: 200px;

    img {
        width: 100%;
        height: auto;
        display: block;
    }

    .kv-label {
        padding: 8px 12px;
        text-align: center;
        font-size: 13px;
        font-weight: 500;
        color: #333;
        background: #f8f9fa;
    }
`;

const KVLoadingState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #666;

    .spinner-container {
        margin-bottom: 12px;
    }
`;

// List of winner project IDs for FD100 2025
const WINNER_PROJECT_IDS = [
    '67a332e21b103d938bafb0dc', '67a0b8331b103d938bafa954', '67aa1011740d844a3d633e93',
    '6829daa5754f51df2ef9141c', '67e6c18043d5609350e381a8', '67b5e6146890f1a147192905',
    '67be4a58b1ba28c3144aeb73', '67bf2330b1ba28c3144af8d2', '67c090dcdb48cb16a13c5b43',
    '677268097ec5d90c89d6fe63', '67c6b416db48cb16a13c73c7', '67ab652c07736060460b3b75',
    '68348986754f51df2ef9b87c', '67d15108d95ccf24eaf191df', '67d7d38c43d5609350e242a1',
    '67d82d2243d5609350e24c5e', '662adc507159e5b1b1da7c75', '67d8718c43d5609350e25c38',
    '67d1977243d5609350e218f2', '682b5c5f754f51df2ef93bbc', '67da910a43d5609350e274d7',
    '67e1864b43d5609350e2baeb', '67daebf343d5609350e27a9e', '67dd585043d5609350e28e57',
    '67dfeb0e43d5609350e2a360', '67e1452a43d5609350e2b066', '678675e2de3ed4a7001ab532',
    '67e2d05943d5609350e2cf97', '67e3e1cb43d5609350e304e5', '67e3e81543d5609350e308de',
    '67e3f02643d5609350e30a0a', '67e42d7043d5609350e32539', '67e42e5543d5609350e32570',
    '67e45c9843d5609350e329c0', '67e529f743d5609350e33a8c', '67e53aab43d5609350e340c3',
    '67e56ab043d5609350e3490f', '67e5764d43d5609350e34cb5', '67e57f5143d5609350e3519a',
    '67e5864c43d5609350e353a3', '67e58da443d5609350e3561d', '67e58ebc43d5609350e35665',
    '67e5ab6b43d5609350e35905', '67e6828d43d5609350e36b6a', '67e6acaf43d5609350e37832',
    '67e51ddb43d5609350e33667', '67e6c0a843d5609350e380cf', '67e6d53a43d5609350e38725',
    '67e6dfff43d5609350e38900', '67e7003c43d5609350e38bfc', '6834625c754f51df2ef9a4b1',
    '67ea99b76b7945ed1a576bae', '67f90250a0262e085d37088f', '67e1741a43d5609350e2b608',
    '67fe7a6c9167c049fff4e37e', '67ab6ade07736060460b3c16', '6810a343264e7745556acae1',
    '6800f4c59167c049fff51f5d', '6800b8529167c049fff50e47', '6800e76e9167c049fff5197f',
    '6800ef849167c049fff51c6f', '6800fad59167c049fff52224', '680113739167c049fff52d73',
    '680106139167c049fff525af', '680125219167c049fff53405', '680957ab9167c049fff5a7c2',
    '681164d36c8fbfe5e3c91e9e', '68020ef79167c049fff54015', '68025d069167c049fff56132',
    '680b728c9167c049fff5c8f7', '680249249167c049fff55a19', '68024ed89167c049fff55b90',
    '68025b1a9167c049fff56037', '680270379167c049fff566f5', '680274c09167c049fff567c8',
    '680275419167c049fff567de', '68027b389167c049fff56954', '680291609167c049fff56cad',
    '6802a3fc9167c049fff56f5f', '6849787b17dde4ca4c4bf043', '6849435a17dde4ca4c4be251',
    '6807cb759167c049fff58dd4', '6808c3f59167c049fff599c7', '6808c3859167c049fff599b1',
    '680fa4b19167c049fff5deee', '6801e2f49167c049fff53ce1', '680a54a09167c049fff5bbc4',
    '680b94b29167c049fff5cd49', '6810df4a264e7745556ad75d', '680fa3f89167c049fff5deb1',
    '680fa844a71ddf126b77b977', '680fa9f09167c049fff5e027', '6810c9b9264e7745556acf4d',
    '681125b66c8fbfe5e3c91c62', '6818cac19e656d77bc6c72fa', '6818d27e9e656d77bc6c75a3',
    '6818dbdf9e656d77bc6c78a0', '682d97b2754f51df2ef96070', '67fccffaa0262e085d370ddc',
    '680271809167c049fff5674a'
];

const PartenairesUploadLogo = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [partenaire, setPartenaire] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState(null);

    // File handling
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);

    // KV preview
    const [sampleProject, setSampleProject] = useState(null);
    const [kvPreviews, setKvPreviews] = useState({ normal: null, inverted: null });
    const [generatingKv, setGeneratingKv] = useState(false);
    const [definitiveNames, setDefinitiveNames] = useState({});

    const partenaireId = searchParams.get('id');

    useEffect(() => {
        if (partenaireId) {
            fetchPartenaire();
        } else {
            setError('ID de partenaire manquant');
            setLoading(false);
        }
    }, [partenaireId]);

    const fetchPartenaire = async () => {
        try {
            setLoading(true);
            setError(null);

            const { data, error: fetchError } = await supabase
                .from('fd100_partenaires')
                .select('*')
                .eq('id', partenaireId)
                .single();

            if (fetchError || !data) {
                setError('Partenaire non trouvé');
                setLoading(false);
                return;
            }

            setPartenaire(data);

            // Fetch definitive project names from Supabase
            const { data: projectNamesData } = await supabase
                .from('fd100_project_names')
                .select('*')
                .eq('event_id', data.event_id);

            // Create a lookup map for definitive names
            const namesMap = {};
            if (projectNamesData) {
                projectNamesData.forEach(item => {
                    namesMap[item.project_id] = {
                        name_fr: item.name_definitive_fr,
                        name_en: item.name_definitive_en,
                        designer: item.designer_definitive
                    };
                });
            }
            setDefinitiveNames(namesMap);

            // Fetch one sample project for KV preview
            const linkedProjectIds = data.linked_project_ids || [];
            const winningLinkedIds = linkedProjectIds.filter(id => WINNER_PROJECT_IDS.includes(id));

            if (winningLinkedIds.length > 0) {
                try {
                    const response = await api.getParticipatingUsersProjectData({ eventId: data.event_id });

                    // Find the first matching project
                    for (const user of response.data) {
                        if (user.participation.data.projets) {
                            for (const projectId of user.participation.data.projets) {
                                if (winningLinkedIds.includes(projectId)) {
                                    const project = user.company.projects.find(p => p._id === projectId);
                                    if (project && project.status === "validé") {
                                        setSampleProject({
                                            project,
                                            user
                                        });
                                        return; // Found one, stop searching
                                    }
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error fetching sample project:', err);
                    // Continue without sample project - KV preview will not be available
                }
            }
        } catch (err) {
            console.error('Error fetching partenaire:', err);
            setError('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, []);

    const handleFileSelect = (file) => {
        if (!file) return;

        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Format non supporté. Veuillez utiliser PNG, JPG, SVG ou WebP.');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert('Le fichier est trop volumineux. Taille maximum: 10 Mo.');
            return;
        }

        setSelectedFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onload = async (e) => {
            const dataUrl = e.target.result;
            setPreviewUrl(dataUrl);

            // Generate KV previews immediately with the dropped logo
            if (sampleProject) {
                setGeneratingKv(true);
                setKvPreviews({ normal: null, inverted: null });
                try {
                    const editedData = getEditedDataForKV(sampleProject.project);
                    const [normalKv, invertedKv] = await Promise.all([
                        generateFD100KVDataURL(sampleProject, false, 'cover', editedData, dataUrl, partenaire?.name),
                        generateFD100KVDataURL(sampleProject, true, 'cover', editedData, dataUrl, partenaire?.name)
                    ]);
                    setKvPreviews({ normal: normalKv, inverted: invertedKv });
                } catch (err) {
                    console.error('Error generating KV previews:', err);
                }
                setGeneratingKv(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !partenaire) return;

        setUploading(true);

        try {
            // Generate unique filename
            const fileExt = selectedFile.name.split('.').pop();
            const fileName = `${partenaire.event_id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

            // Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from('fd100-partenaires-logos')
                .upload(fileName, selectedFile);

            if (uploadError) {
                console.error('Upload error:', uploadError);
                alert('Erreur lors de l\'upload. Veuillez réessayer.');
                setUploading(false);
                return;
            }

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('fd100-partenaires-logos')
                .getPublicUrl(fileName);

            // Update partenaire with new logo
            const existingLogos = partenaire.logo_urls || [];
            const newLogoUrls = [publicUrl, ...existingLogos]; // New logo becomes primary

            const { error: updateError } = await supabase
                .from('fd100_partenaires')
                .update({
                    logo_urls: newLogoUrls,
                    has_uploaded_logo: true,
                    logo_uploaded_at: new Date().toISOString()
                })
                .eq('id', partenaire.id);

            if (updateError) {
                console.error('Update error:', updateError);
                alert('Erreur lors de la mise à jour. Veuillez réessayer.');
                setUploading(false);
                return;
            }

            setUploadedLogoUrl(publicUrl);
            setSuccess(true);
            // KV previews are already generated on file drop, no need to regenerate
        } catch (err) {
            console.error('Error uploading logo:', err);
            alert('Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setUploading(false);
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'fabricant': return 'Fabricant';
            case 'ecole': return 'École';
            case 'distributeur': return 'Distributeur';
            case 'editeur': return 'Galerie / Éditeur';
            default: return 'Partenaire';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'fabricant': return '🏭';
            case 'ecole': return '🎓';
            case 'distributeur': return '🏪';
            case 'editeur': return '🖼';
            default: return '🤝';
        }
    };

    // Helper to get definitive project name
    const getProjectName = (project) => {
        if (!project) return 'Projet';
        const definitive = definitiveNames[project._id];
        if (definitive) {
            return definitive.name_fr || definitive.name_en;
        }
        return project.name_en || project.name || 'Projet';
    };

    // Helper to get edited data for KV generation
    const getEditedDataForKV = (project) => {
        if (!project) return null;
        const definitive = definitiveNames[project._id];
        if (definitive) {
            return {
                projectName: definitive.name_fr || definitive.name_en,
                creatorName: definitive.designer
            };
        }
        return null;
    };

    const existingLogo = partenaire?.logo_urls?.[0] || partenaire?.logo_url;

    if (loading) {
        return (
            <Section className="padding">
                <br /><br />
                <Spinner />
            </Section>
        );
    }

    if (error) {
        return (
            <Section className="padding">
                <Container>
                    <LogoHeader>
                        <img src="/img/logo.png" alt="Le French Design" />
                    </LogoHeader>
                    <Card>
                        <ErrorContainer>
                            <div className="icon">⚠️</div>
                            <div className="title">Erreur</div>
                            <div className="message">{error}</div>
                        </ErrorContainer>
                    </Card>
                </Container>
            </Section>
        );
    }

    if (success) {
        const projectName = getProjectName(sampleProject?.project);

        return (
            <Section className="padding">
                <Container>
                    <LogoHeader>
                        <img src="/img/logo.png" alt="Le French Design" />
                    </LogoHeader>
                    <Card>
                        <SuccessContainer>
                            <div className="success-icon">
                                <svg viewBox="0 0 24 24">
                                    <path d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2>Logo envoyé avec succès !</h2>
                            <p className="message">
                                Merci pour votre contribution. Votre logo sera utilisé sur les Key Visuals
                                des projets lauréats auxquels vous avez contribué.
                            </p>
                            {uploadedLogoUrl && (
                                <img src={uploadedLogoUrl} alt="Logo uploadé" className="logo-preview" />
                            )}

                            {/* KV Preview Section */}
                            {sampleProject && (
                                <KVPreviewSection>
                                    <h3>🎨 Aperçu des Key Visuals</h3>
                                    <p className="kv-subtitle">
                                        Voici un aperçu de votre logo sur le Key Visual du projet « {projectName} »
                                    </p>

                                    {generatingKv ? (
                                        <KVLoadingState>
                                            <div className="spinner-container">
                                                <Spinner />
                                            </div>
                                            Génération des Key Visuals...
                                        </KVLoadingState>
                                    ) : (
                                        <KVGrid>
                                            {kvPreviews.normal && (
                                                <KVCard>
                                                    <img src={kvPreviews.normal} alt="Key Visual Gris" />
                                                    <div className="kv-label">Version Grise</div>
                                                </KVCard>
                                            )}
                                            {kvPreviews.inverted && (
                                                <KVCard>
                                                    <img src={kvPreviews.inverted} alt="Key Visual Bleu" />
                                                    <div className="kv-label">Version Bleue</div>
                                                </KVCard>
                                            )}
                                        </KVGrid>
                                    )}
                                </KVPreviewSection>
                            )}

                            <div className="next-steps">
                                <h3>Prochaines étapes</h3>
                                <p>
                                    Votre logo apparaîtra sur les Key Visuals disponibles dans votre espace partenaire,
                                    ainsi que sur le site internet du French Design et dans le catalogue de l'édition 2026.
                                    N'oubliez pas d'attendre le <strong>9 avril 2026</strong> pour toute communication publique.
                                </p>
                            </div>
                        </SuccessContainer>
                        <Footer>
                            120 avenue Ledru-Rollin – F 75011 Paris<br />
                            <a href="https://lefrenchdesign.org" target="_blank" rel="noopener noreferrer">lefrenchdesign.org</a>
                        </Footer>
                    </Card>
                </Container>
            </Section>
        );
    }

    return (
        <Section className="padding">
            <Container>
                <LogoHeader>
                    <img src="/img/logo.png" alt="Le French Design" />
                </LogoHeader>

                <Card>
                    <CardHeader>
                        <h1>Envoi de votre logo</h1>
                        <div className="subtitle">Le FRENCH DESIGN 100 - 4ème édition</div>
                    </CardHeader>

                    <CardBody>
                        <PartenaireInfo>
                            <div className="name">{partenaire?.name}</div>
                            <div className="badge">
                                {getTypeIcon(partenaire?.type)} {getTypeLabel(partenaire?.type)}
                            </div>
                        </PartenaireInfo>

                        {existingLogo && (
                            <ExistingLogo>
                                <div className="label">Logo actuel</div>
                                <img src={existingLogo} alt="Logo actuel" />
                            </ExistingLogo>
                        )}

                        <Instructions>
                            <h3>📋 Instructions</h3>
                            <ul>
                                <li>Formats acceptés : <strong>PNG</strong> (recommandé), JPG, SVG, WebP</li>
                                <li>Pour une meilleure qualité, privilégiez un <strong>fond transparent</strong></li>
                                <li>Taille maximale : 10 Mo</li>
                                <li>Votre logo sera visible sur les visuels, le site internet et le catalogue</li>
                            </ul>
                        </Instructions>

                        <DropZone
                            isDragActive={isDragActive}
                            hasFile={!!selectedFile}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('logo-input').click()}
                        >
                            <input
                                type="file"
                                id="logo-input"
                                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                                onChange={handleInputChange}
                            />
                            {!selectedFile ? (
                                <>
                                    <span className="icon">📤</span>
                                    <div className="main-text">
                                        {isDragActive ? 'Déposez votre logo ici' : 'Glissez votre logo ici'}
                                    </div>
                                    <div className="sub-text">ou cliquez pour sélectionner un fichier</div>
                                </>
                            ) : (
                                <>
                                    <span className="icon">✅</span>
                                    <div className="main-text">Fichier sélectionné</div>
                                    <div className="sub-text">Cliquez pour changer de fichier</div>
                                </>
                            )}
                        </DropZone>

                        {previewUrl && (
                            <PreviewContainer>
                                <div className="preview-label">
                                    ✨ Aperçu de votre logo
                                </div>
                                <img src={previewUrl} alt="Aperçu" className="preview-image" />
                                <div className="file-name">
                                    📎 {selectedFile?.name}
                                </div>
                                <button
                                    className="change-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFile(null);
                                        setPreviewUrl(null);
                                        setKvPreviews({ normal: null, inverted: null });
                                    }}
                                >
                                    Changer de fichier
                                </button>

                                {/* KV Preview - shown immediately after logo drop */}
                                {sampleProject && (
                                    <KVPreviewSection style={{ marginTop: '24px' }}>
                                        <h3>🎨 Aperçu des Key Visuals</h3>
                                        <p className="kv-subtitle">
                                            Voici un aperçu de votre logo sur le Key Visual du projet « {getProjectName(sampleProject.project)} »
                                        </p>

                                        {generatingKv ? (
                                            <KVLoadingState>
                                                <div className="spinner-container">
                                                    <Spinner />
                                                </div>
                                                Génération des Key Visuals...
                                            </KVLoadingState>
                                        ) : (
                                            <KVGrid>
                                                {kvPreviews.normal && (
                                                    <KVCard>
                                                        <img src={kvPreviews.normal} alt="Key Visual Gris" />
                                                        <div className="kv-label">Version Grise</div>
                                                    </KVCard>
                                                )}
                                                {kvPreviews.inverted && (
                                                    <KVCard>
                                                        <img src={kvPreviews.inverted} alt="Key Visual Bleu" />
                                                        <div className="kv-label">Version Bleue</div>
                                                    </KVCard>
                                                )}
                                            </KVGrid>
                                        )}
                                    </KVPreviewSection>
                                )}
                            </PreviewContainer>
                        )}

                        <SubmitButton
                            disabled={!selectedFile || uploading}
                            onClick={handleUpload}
                        >
                            {uploading ? (
                                <>
                                    <Spinner /> Envoi en cours...
                                </>
                            ) : (
                                <>
                                    📤 Envoyer mon logo
                                </>
                            )}
                        </SubmitButton>
                    </CardBody>

                    <Footer>
                        En cas de question, contactez-nous à{' '}
                        <a href="mailto:troyes@via.fr">troyes@via.fr</a><br />
                        120 avenue Ledru-Rollin – F 75011 Paris |{' '}
                        <a href="https://lefrenchdesign.org" target="_blank" rel="noopener noreferrer">lefrenchdesign.org</a>
                    </Footer>
                </Card>
            </Container>
        </Section>
    );
};

export default PartenairesUploadLogo;
